/**
 * WorkflowDialogShell - Casca visual compartilhada dos modais de Workflow
 * -----------------------------------------------------------------------------
 * Padroniza o leiaute dos modais do módulo (criação, edição/configuração e
 * exclusão) seguindo o modelo do modal de anotações do chat
 * (`ContactNoteEditorDialog`):
 *
 *   - Header premium: faixa com gradiente `from-primary/5`, tile de ícone
 *     arredondado + título `text-base` e descrição curta `text-xs`;
 *   - Corpo rolável entre header e footer (footer nunca some da tela);
 *   - Footer fixo compacto (`bg-muted/10`) — os botões ficam a cargo do
 *     consumidor via prop `footer`.
 *
 * Uso:
 *   <WorkflowDialogShell
 *     open={aberto} onOpenChange={setAberto}
 *     icon={GitBranch} title="Novo processo" description="..."
 *     footer={<Button .../>}
 *   >
 *     ...campos...
 *   </WorkflowDialogShell>
 *
 * @version 1.0.0
 * RELEASE: 2026-08-31 v1.0.0 - Criação inicial (padronização dos modais de
 *          workflow no modelo do modal de anotações do chat).
 */

import type { ComponentType, ReactNode } from 'react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

/** Ícone aceito pelo shell (qualquer ícone lucide) */
type Icone = ComponentType<{ className?: string }>;

interface WorkflowDialogShellProps {
  open: boolean;
  onOpenChange: (aberto: boolean) => void;
  /** Ícone exibido no tile do header */
  icon: Icone;
  /** Título do modal */
  title: string;
  /** Descrição curta exibida sob o título (opcional) */
  description?: string;
  /** Tom visual: 'destructive' tinge o tile/ícone para ações perigosas */
  tone?: 'default' | 'destructive';
  /** Largura máxima do dialog (padrão: sm:max-w-2xl) */
  maxWidthClass?: string;
  /** Conteúdo rolável (campos do formulário) */
  children: ReactNode;
  /** Rodapé fixo (botões de ação) */
  footer: ReactNode;
}

export function WorkflowDialogShell({
  open,
  onOpenChange,
  icon: Icon,
  title,
  description,
  tone = 'default',
  maxWidthClass = 'sm:max-w-2xl',
  children,
  footer,
}: WorkflowDialogShellProps) {
  const destrutivo = tone === 'destructive';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'flex max-h-[85vh] flex-col gap-0 overflow-hidden p-0',
          maxWidthClass,
        )}
      >
        {/* ===== HEADER PREMIUM (padrão ContactNoteEditorDialog) ===== */}
        <div
          className={cn(
            'border-b px-6 py-4',
            destrutivo
              ? 'bg-gradient-to-r from-destructive/10 via-destructive/5 to-transparent'
              : 'bg-gradient-to-r from-primary/5 via-primary/3 to-transparent',
          )}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <div
                className={cn(
                  'flex h-7 w-7 items-center justify-center rounded-lg',
                  destrutivo ? 'bg-destructive/10' : 'bg-primary/10',
                )}
              >
                <Icon
                  className={cn(
                    'h-3.5 w-3.5',
                    destrutivo ? 'text-destructive' : 'text-primary',
                  )}
                />
              </div>
              {title}
            </DialogTitle>
            {description && (
              <DialogDescription className="text-xs text-muted-foreground">
                {description}
              </DialogDescription>
            )}
          </DialogHeader>
        </div>

        {/* ===== CORPO ROLÁVEL (footer fica sempre visível) ===== */}
        <div className="-mr-1 flex-1 overflow-y-auto pr-1">
          <div className="p-5">{children}</div>
        </div>

        {/* ===== FOOTER FIXO COMPACTO ===== */}
        <div className="flex flex-wrap items-center justify-end gap-2 border-t bg-muted/10 px-6 py-3">
          {footer}
        </div>
      </DialogContent>
    </Dialog>
  );
}
